import { create } from 'zustand';
import { Timestamp, doc, getDoc, onSnapshot } from 'firebase/firestore';
import type { CheckIn, CheckinAnswerValue, UserStreaks, AIAnalysis } from '../types/checkin';
import { saveCheckIn, loadTodayCheckIns, loadTodayMorningCheckIn, formatDateKey } from '../services/firebase/checkin';
import { db } from '../services/firebase/config';
import { useAuthStore } from './authStore';

/**
 * Validates UserStreaks data from Firestore
 * @param data - Raw data from Firestore document
 * @returns Validated UserStreaks object or null if invalid
 */
function validateUserStreaks(data: any): UserStreaks | null {
  // Check if data exists and has required structure
  if (!data || typeof data !== 'object') {
    return null;
  }

  // Check for required top-level fields
  if (!data.morning || !data.nightly || !data.fullDay) {
    console.warn('Invalid streaks data structure:', data);
    return null;
  }

  // Basic validation of streak data structure
  const validateStreakData = (streak: any) => {
    return streak
      && typeof streak.current === 'number'
      && typeof streak.longest === 'number'
      && typeof streak.lastCompletedDate === 'string';
  };

  if (!validateStreakData(data.morning) ||
      !validateStreakData(data.nightly) ||
      !validateStreakData(data.fullDay)) {
    console.warn('Invalid streak data fields:', data);
    return null;
  }

  return data as UserStreaks;
}

interface CheckinState {
  // Current check-in being filled out
  currentCheckIn: Partial<CheckIn> | null;

  // Today's status
  todayMorningComplete: boolean;
  todayNightlyComplete: boolean;
  todayCheckInExists: boolean;
  existingCheckIn: CheckIn | null;

  // Streaks
  streaks: UserStreaks | null;

  // AI Analysis
  lastAIAnalysis: AIAnalysis | null;

  // Loading states
  isLoadingMorning: boolean;
  isLoadingNightly: boolean;
  isLoadingStreaks: boolean;

  // Listener unsubscribe functions (for cleanup)
  unsubscribeTodayCheckIn: (() => void) | null;
  unsubscribeStreaks: (() => void) | null;

  // Morning Check-In Actions
  startMorningCheckIn: (template: 'daily-intentions' | 'step-11' | 'custom') => Promise<void>;
  updateMorningResponse: (questionId: string, value: CheckinAnswerValue) => void;
  submitCheckIn: () => Promise<void>;

  // Status Actions
  loadTodayStatus: () => Promise<void>;
  loadStreaks: () => Promise<void>;

  // Real-time Listener Actions
  subscribeTodayCheckIn: () => void;
  subscribeStreaks: () => void;
  unsubscribeAll: () => void;

  // Utility Actions
  reset: () => void;
}

export const useCheckinStore = create<CheckinState>((set, get) => ({
  // Initial State
  currentCheckIn: null,
  todayMorningComplete: false,
  todayNightlyComplete: false,
  todayCheckInExists: false,
  existingCheckIn: null,
  streaks: null,
  lastAIAnalysis: null,
  isLoadingMorning: false,
  isLoadingNightly: false,
  isLoadingStreaks: false,
  unsubscribeTodayCheckIn: null,
  unsubscribeStreaks: null,

  // Morning Check-In Actions
  startMorningCheckIn: async (template) => {
    // Guard against race conditions from multiple clicks
    if (get().isLoadingMorning) return;

    const { user } = useAuthStore.getState();
    if (!user) {
      console.warn('Cannot start check-in: no authenticated user');
      return;
    }

    set({ isLoadingMorning: true });

    try {
      // Check if check-in already exists today
      const existingCheckIn = await loadTodayMorningCheckIn(user.uid);

      if (existingCheckIn) {
        // Check-in exists - enter edit mode
        set({
          currentCheckIn: {
            ...existingCheckIn,
            editCount: (existingCheckIn.editCount || 0) + 1,
          },
          existingCheckIn,
          todayCheckInExists: true,
          isLoadingMorning: false,
        });
      } else {
        // New check-in
        set({
          currentCheckIn: {
            type: 'morning',
            templateUsed: template,
            responses: {},
            sharedWithSponsors: [],
            date: Timestamp.now(),
            editCount: 0,
          },
          existingCheckIn: null,
          todayCheckInExists: false,
          isLoadingMorning: false,
        });
      }
    } catch (error) {
      console.error('Error starting morning check-in:', error);
      set({ isLoadingMorning: false });
    }
  },

  updateMorningResponse: (questionId, value) => {
    const { currentCheckIn } = get();
    if (!currentCheckIn || !currentCheckIn.responses) return;

    set({
      currentCheckIn: {
        ...currentCheckIn,
        responses: {
          ...currentCheckIn.responses,
          [questionId]: {
            questionId,
            questionText: '', // Will be filled in later when we have the question
            questionType: 'text', // Will be filled in later
            answer: value,
            isFromSponsor: false,
          },
        },
      },
    });
  },

  submitCheckIn: async () => {
    const { currentCheckIn } = get();
    if (!currentCheckIn) return;

    // Get user ID from auth store
    const { user } = useAuthStore.getState();
    if (!user) {
      console.error('Cannot submit check-in: no authenticated user');
      throw new Error('No authenticated user');
    }

    set({ isLoadingMorning: true });
    try {
      // Complete the check-in data with required fields
      const completedCheckIn: CheckIn = {
        id: currentCheckIn.id || crypto.randomUUID(),
        date: currentCheckIn.date || Timestamp.now(),
        completedAt: Timestamp.now(),
        lastUpdatedAt: Timestamp.now(),
        editCount: currentCheckIn.editCount || 0,
        type: 'morning',
        templateUsed: currentCheckIn.templateUsed || 'daily-intentions',
        responses: currentCheckIn.responses || {},
        sharedWithSponsors: currentCheckIn.sharedWithSponsors || [],
      };

      // Save to Firebase
      await saveCheckIn(user.uid, completedCheckIn);
      console.log('Morning check-in submitted:', completedCheckIn);

      set({
        todayMorningComplete: true,
        todayCheckInExists: true,
        existingCheckIn: completedCheckIn,
        isLoadingMorning: false,
        currentCheckIn: null,
      });
    } catch (error) {
      console.error('Error submitting morning check-in:', error);
      set({ isLoadingMorning: false });
      throw error;
    }
  },

  // Status Actions
  loadTodayStatus: async () => {
    const { user } = useAuthStore.getState();
    if (!user) {
      console.warn('Cannot load today status: no authenticated user');
      return;
    }

    // If listener is active, don't need to load (listener handles it)
    const { unsubscribeTodayCheckIn } = get();
    if (unsubscribeTodayCheckIn) {
      console.log('Today check-in listener is active, skipping manual load');
      return;
    }

    set({ isLoadingMorning: true });
    try {
      const today = await loadTodayCheckIns(user.uid);
      set({
        todayMorningComplete: !!today.morning,
        currentCheckIn: today.morning || null,
        isLoadingMorning: false,
      });
    } catch (error) {
      console.error('Error loading today status:', error);
      set({ isLoadingMorning: false });
    }
  },

  loadStreaks: async () => {
    const { user } = useAuthStore.getState();
    if (!user) {
      console.warn('Cannot load streaks: no authenticated user');
      return;
    }

    // If listener is active, don't need to load (listener handles it)
    const { unsubscribeStreaks } = get();
    if (unsubscribeStreaks) {
      console.log('Streaks listener is active, skipping manual load');
      return;
    }

    set({ isLoadingStreaks: true });
    try {
      const docRef = doc(db, 'users', user.uid, 'streaks', 'current');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const streaksData = validateUserStreaks(docSnap.data());
        set({
          streaks: streaksData,
          isLoadingStreaks: false,
        });
      } else {
        set({
          streaks: null,
          isLoadingStreaks: false,
        });
      }
    } catch (error) {
      console.error('Error loading streaks:', error);
      set({ isLoadingStreaks: false });
    }
  },

  // Real-time Listener Actions
  subscribeTodayCheckIn: () => {
    const { user } = useAuthStore.getState();
    if (!user) {
      console.warn('Cannot subscribe to today check-in: no authenticated user');
      return;
    }

    // Unsubscribe from previous listener if exists
    const { unsubscribeTodayCheckIn } = get();
    if (unsubscribeTodayCheckIn) {
      console.log('Unsubscribing from existing today check-in listener');
      unsubscribeTodayCheckIn();
    }

    const dateKey = formatDateKey(new Date());
    const docRef = doc(db, 'users', user.uid, 'checkins', dateKey);

    console.log('Subscribing to today\'s check-in document:', dateKey);

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          console.log('Today check-in updated:', data);
          set({
            todayMorningComplete: !!data.morning,
            todayNightlyComplete: !!data.nightly,
            lastAIAnalysis: data.nightly?.aiAnalysis || null,
          });
        } else {
          console.log('No check-in for today yet');
          set({
            todayMorningComplete: false,
            todayNightlyComplete: false,
            lastAIAnalysis: null,
          });
        }
      },
      (error) => {
        console.error('Error listening to today\'s check-ins:', error);
      }
    );

    set({ unsubscribeTodayCheckIn: unsubscribe });
  },

  subscribeStreaks: () => {
    const { user } = useAuthStore.getState();
    if (!user) {
      console.warn('Cannot subscribe to streaks: no authenticated user');
      return;
    }

    // Unsubscribe from previous listener if exists
    const { unsubscribeStreaks } = get();
    if (unsubscribeStreaks) {
      console.log('Unsubscribing from existing streaks listener');
      unsubscribeStreaks();
    }

    const docRef = doc(db, 'users', user.uid, 'streaks', 'current');

    console.log('Subscribing to user streaks document');

    const unsubscribe = onSnapshot(
      docRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const streaksData = validateUserStreaks(snapshot.data());
          if (streaksData) {
            console.log('Streaks updated:', streaksData);
            set({ streaks: streaksData });
          } else {
            console.warn('Received invalid streaks data from Firestore');
          }
        } else {
          console.log('No streaks document exists yet');
          set({ streaks: null });
        }
      },
      (error) => {
        console.error('Error listening to streaks:', error);
      }
    );

    set({ unsubscribeStreaks: unsubscribe });
  },

  unsubscribeAll: () => {
    console.log('Unsubscribing from all check-in listeners');
    const { unsubscribeTodayCheckIn, unsubscribeStreaks } = get();

    if (unsubscribeTodayCheckIn) {
      unsubscribeTodayCheckIn();
      set({ unsubscribeTodayCheckIn: null });
    }

    if (unsubscribeStreaks) {
      unsubscribeStreaks();
      set({ unsubscribeStreaks: null });
    }
  },

  // Utility Actions
  reset: () => {
    // Cleanup listeners first
    const { unsubscribeAll } = get();
    unsubscribeAll();

    // Reset state
    set({
      currentCheckIn: null,
      todayMorningComplete: false,
      todayNightlyComplete: false,
      todayCheckInExists: false,
      existingCheckIn: null,
      streaks: null,
      lastAIAnalysis: null,
      isLoadingMorning: false,
      isLoadingNightly: false,
      isLoadingStreaks: false,
    });
  },
}));
